/**
 * The island characters and the shop. Shared by the Worker (which checks the price and the balance, so a hidden
 * button is never the only guard) and the app (which draws it). The currency is play tokens, never money.
 */
export const CHARACTER_IDS = ['coqui', 'cotorra', 'tortuga', 'caballito', 'cangrejo', 'manati', 'gallo', 'iguana'] as const
export type CharacterId = (typeof CHARACTER_IDS)[number]
export type Slot = 'hat' | 'glasses' | 'extra' | 'bg' | 'tone'
export interface ShopItem { id: string; slot: Slot; price: number; title: { en: string; es: string } }

export const SLOTS: { slot: Slot; title: { en: string; es: string } }[] = [
  { slot: 'hat', title: { en: 'Hats', es: 'Sombreros' } },
  { slot: 'glasses', title: { en: 'Glasses', es: 'Gafas' } },
  { slot: 'extra', title: { en: 'Extras', es: 'Accesorios' } },
  { slot: 'bg', title: { en: 'Backgrounds', es: 'Fondos' } },
  { slot: 'tone', title: { en: 'Colors', es: 'Colores' } },
]

/** Everyday things are cheap; the crown is a goal. A coconut brings a parrot (2 tokens), so 20 tokens is about ten good exchanges. */
export const SHOP: ShopItem[] = [
  { id: 'flor', slot: 'hat', price: 20, title: { en: 'Flamboyán flower', es: 'Flor de flamboyán' } },
  { id: 'gorra', slot: 'hat', price: 30, title: { en: 'Cap', es: 'Gorra' } },
  { id: 'pava', slot: 'hat', price: 50, title: { en: 'Jíbaro straw hat', es: 'Pava jíbara' } },
  { id: 'corona', slot: 'hat', price: 150, title: { en: 'Crown', es: 'Corona' } },
  { id: 'espejuelos', slot: 'glasses', price: 20, title: { en: 'Specs', es: 'Espejuelos' } },
  { id: 'gafas', slot: 'glasses', price: 35, title: { en: 'Sunglasses', es: 'Gafas de sol' } },
  { id: 'pajarita', slot: 'extra', price: 25, title: { en: 'Bow tie', es: 'Corbatín' } },
  { id: 'bufanda', slot: 'extra', price: 25, title: { en: 'Red scarf', es: 'Bufanda roja' } },
  { id: 'collar', slot: 'extra', price: 30, title: { en: 'Flower necklace', es: 'Collar de flores' } },
  { id: 'bandana', slot: 'extra', price: 35, title: { en: 'Bandana', es: 'Bandana' } },
  { id: 'cadena', slot: 'extra', price: 70, title: { en: 'Gold chain', es: 'Cadena de oro' } },
  { id: 'hojas', slot: 'bg', price: 30, title: { en: 'Palm leaves', es: 'Hojas de palma' } },
  { id: 'atardecer', slot: 'bg', price: 40, title: { en: 'Sunset', es: 'Atardecer' } },
  { id: 'mar', slot: 'bg', price: 40, title: { en: 'The sea', es: 'El mar' } },
  { id: 'noche', slot: 'bg', price: 60, title: { en: 'Island night', es: 'Noche en la isla' } },
  { id: 'fiesta', slot: 'hat', price: 35, title: { en: 'Party hat', es: 'Gorro de fiesta' } },
  { id: 'cocinero', slot: 'hat', price: 35, title: { en: 'Chef hat', es: 'Gorro de cocinero' } },
  { id: 'casco', slot: 'hat', price: 30, title: { en: 'Hard hat', es: 'Casco' } },
  { id: 'orejas', slot: 'hat', price: 45, title: { en: 'Cat ears', es: 'Orejas de gato' } },
  { id: 'pirata', slot: 'hat', price: 80, title: { en: 'Pirate hat', es: 'Sombrero pirata' } },
  { id: 'aureola', slot: 'hat', price: 60, title: { en: 'Halo', es: 'Aureola' } },
  { id: 'corazones', slot: 'glasses', price: 45, title: { en: 'Heart shades', es: 'Gafas de corazón' } },
  { id: 'estrellas', slot: 'glasses', price: 45, title: { en: 'Star shades', es: 'Gafas de estrella' } },
  { id: 'buceo', slot: 'glasses', price: 50, title: { en: 'Dive goggles', es: 'Gafas de buceo' } },
  { id: 'monoculo', slot: 'glasses', price: 60, title: { en: 'Monocle', es: 'Monóculo' } },
  { id: 'corbata', slot: 'extra', price: 30, title: { en: 'Necktie', es: 'Corbata' } },
  { id: 'medalla', slot: 'extra', price: 40, title: { en: 'Gold medal', es: 'Medalla de oro' } },
  { id: 'audifonos', slot: 'extra', price: 45, title: { en: 'Headphones', es: 'Audífonos' } },
  { id: 'perlas', slot: 'extra', price: 50, title: { en: 'Pearls', es: 'Perlas' } },
  { id: 'salvavidas', slot: 'extra', price: 55, title: { en: 'Life ring', es: 'Salvavidas' } },
  { id: 'confeti', slot: 'bg', price: 35, title: { en: 'Confetti', es: 'Confeti' } },
  { id: 'corazonesbg', slot: 'bg', price: 35, title: { en: 'Hearts', es: 'Corazones' } },
  { id: 'playa', slot: 'bg', price: 40, title: { en: 'The beach', es: 'La playa' } },
  { id: 'estrellasbg', slot: 'bg', price: 45, title: { en: 'Starry sky', es: 'Cielo de estrellas' } },
  { id: 'arcoiris', slot: 'bg', price: 50, title: { en: 'Rainbow', es: 'Arcoíris' } },
  { id: 'sol', slot: 'tone', price: 25, title: { en: 'Sunny', es: 'Soleado' } },
  { id: 'profundo', slot: 'tone', price: 25, title: { en: 'Deep', es: 'Profundo' } },
  { id: 'menta', slot: 'tone', price: 35, title: { en: 'Mint', es: 'Menta' } },
  { id: 'oceano', slot: 'tone', price: 40, title: { en: 'Ocean', es: 'Océano' } },
  { id: 'fresa', slot: 'tone', price: 40, title: { en: 'Berry', es: 'Fresa' } },
  { id: 'dorado', slot: 'tone', price: 50, title: { en: 'Golden', es: 'Dorado' } },
]
export const itemById = (id: string) => SHOP.find(i => i.id === id)
export const isCharacter = (v: unknown): v is CharacterId => typeof v === 'string' && (CHARACTER_IDS as readonly string[]).includes(v)

/** Boost: a listing is shown first in Browse for a few days. Paid in play tokens, checked on the server. */
export const BOOST_COST = 15
export const BOOST_DAYS = 3

/** Token packs. In the beta these are PLAY purchases: nothing is charged. Real payments (Apple, Google) are a separate, later decision. */
export interface TokenPack { id: string; tokens: number; bonus: number; priceCents: number; title: { en: string; es: string } }
export const TOKEN_PACKS: TokenPack[] = [
  { id: 'punado', tokens: 50, bonus: 0, priceCents: 199, title: { en: 'Handful', es: 'Puñado' } },
  { id: 'canasto', tokens: 140, bonus: 20, priceCents: 499, title: { en: 'Basket', es: 'Canasto' } },
  { id: 'cofre', tokens: 320, bonus: 70, priceCents: 999, title: { en: 'Chest', es: 'Cofre' } },
]
export const PURCHASES_PER_DAY = 10
